/**
 * showdownMoves.batch3 — batch 3 of the per-move Showdown animation ports.
 *
 * Each entry is a near-verbatim port of the move's script from Pokémon Showdown's
 * `BattleMoveAnims` (`battle-animations-moves.ts`, MIT/CC0), plus the handful of
 * shared `BattleOtherAnims` templates it delegates to (`battle-animations.ts`).
 * Our {@link FxScene} + {@link Actor} mirror Showdown's `scene` + `Sprite`, so the
 * literal `scene.showEffect(...)` / `attacker.anim(...)` / `scene.wait(...)`
 * sequences port unchanged.
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog are used.
 * Where Showdown drew the attacker's own sprite as a streak (`attacker.sp`), our
 * Actor has no sprite-as-effect, so those `showEffect(attacker.sp, …)` calls are
 * omitted — the attacker's own lunge below preserves the read (noted inline). The
 * 5th `{ filter: … }` argument PS passes to a few showEffect calls is dropped (our
 * showEffect signature has no filter slot); the motion is preserved verbatim.
 *
 * Templates this batch needs that are NOT exported by showdownOther (`xattack`,
 * `spinattack`, `selfstatus`, `dance`, `sound`, `flight`) are ported inline here
 * as local consts so this file stays self-contained and owns only itself.
 *
 * Test/runtime separation: imports ONLY TYPES from showdownFx.ts (which pulls in
 * Phaser) — every script is a pure function over the FxScene/Actor interfaces.
 */
import type { FxScene, Actor, MoveAnim } from './showdownFx.js';
import { contactattack, slashattack } from './showdownOther.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Inlined BattleOtherAnims templates used by several scripts below.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.xattack` — a double cross-slash lunge through the defender. */
const xattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 700 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 1000,
    },
    'linear',
  );
  defender.delay(480);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
};

/** PS `BattleOtherAnims.spinattack` — a spinning ballistic contact lunge. */
const spinattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.x, y: defender.y + 60, z: defender.behind(-30), time: 400 },
    'ballistic2',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  scene.wait(500);
};

/** PS `BattleOtherAnims.selfstatus` — two wisps collapse inward on the user. */
const selfstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 0, opacity: 1, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 200 },
    { scale: 0, opacity: 1, time: 500 },
    'linear',
  );
};

/** PS `BattleOtherAnims.dance` — a small left/right shimmy on the user. */
const dance: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10 });
  attacker.anim({ x: attacker.x + 10 });
  attacker.anim({ x: attacker.x });
};

/** PS `BattleOtherAnims.sound` — three wisps ripple out behind the user. */
const sound: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 0 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 150 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 300 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `BattleOtherAnims.flight` — fly off-screen, swoop back through the defender. */
const flight: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: attacker.leftof(-200), y: attacker.y + 80, z: attacker.z, opacity: 0, time: 350 },
    'accel',
  );
  attacker.anim({ x: defender.leftof(-200), y: defender.y + 80, z: defender.z, time: 1 }, 'linear');
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, opacity: 1, time: 350 }, 'accel');
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 700 },
    { scale: 2, opacity: 0, time: 900 },
    'linear',
  );
  attacker.anim({
    x: defender.leftof(100),
    y: defender.y - 40,
    z: defender.z,
    opacity: 0,
    time: 175,
  });
  attacker.anim({ x: attacker.x, y: attacker.y + 40, z: attacker.behind(40), time: 1 });
  attacker.anim({ x: attacker.x, y: attacker.y, z: attacker.z, time: 250 }, 'decel');
  defender.delay(700);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/* ──────────────────────────────────────────────────────────────────────────
 * Ported move scripts.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `flipturn` — water wisps splash + a spinattack lunge. */
const flipturn: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'waterwisp',
    {
      x: defender.x,
      y: defender.y + 80,
      z: defender.behind(-15),
      scale: 1.5,
      opacity: 0.8,
      time: 400,
    },
    { y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x, y: defender.y - 25, z: defender.z, scale: 1, time: 500 },
    { x: defender.x + 50, scale: 0.6, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x, y: defender.y - 25, z: defender.z, scale: 1, time: 500 },
    { x: defender.x - 50, scale: 0.6, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  spinattack(scene, actors);
};

/**
 * PS `fly` — a charging move. Its main `anim` delegates to the `flight` template;
 * its `prepareAnim` lifts and fades the user. Like batch 1's dig/dive, the table
 * below exposes only the main `anim` (resolver `getShowdownAnim` returns a
 * `MoveAnim`); `flyPrepare` is kept for the integration layer that wires charge.
 */
const fly: MoveAnim = flight;
const flyPrepare: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ opacity: 0.2, y: attacker.y + 80, time: 300 }, 'linear');
};
void flyPrepare;

/** PS `focusblast` — a charged electro orb hurls into the defender and bursts. */
const focusblast: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#B84038', 700, 0.6);
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 3, opacity: 0.3 },
    { scale: 0.6, opacity: 1, time: 300 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 3, opacity: 0.3, time: 100 },
    { scale: 0.8, opacity: 0.6, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-30), scale: 0.6, opacity: 0.8, time: 400 },
    { x: defender.x, y: defender.y, z: defender.behind(20), scale: 1, opacity: 0.8, time: 800 },
    'accel',
    'explode',
  );
  defender.delay(800);
  defender.anim({ z: defender.behind(10), time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `focusenergy` → `selfstatus` template. */
const focusenergy: MoveAnim = selfstatus;

/**
 * PS `focuspunch` — a dark wash, impact rings + a fist jab and a hard lunge. PS
 * drew two attacker-sprite streaks (`showEffect(attacker.sp, …)`); omitted (no
 * sprite-as-effect in our engine) — the attacker's lunge below preserves the read.
 */
const focuspunch: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.2);
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 4, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 300 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 2,
      opacity: 0,
      time: 600,
    },
    'linear',
  );
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted.
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `followme` — a `dance` shimmy plus a pointer rising over the user. */
const followme: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [attacker] = actors;
  dance(scene, actors);
  scene.showEffect(
    'pointer',
    { x: attacker.x, y: attacker.y + 30, z: attacker.z, scale: 0.4, opacity: 1, time: 100 },
    { y: attacker.y + 60, opacity: 1, time: 550 },
    'decel',
    'fade',
  );
};

/** PS `forcepalm` — a palm chop sweeps across the defender + a punch lunge. */
const forcepalm: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'rightchop',
    { x: defender.x, y: defender.y, z: defender.z - 10, scale: 0.6, opacity: 0.1, time: 350 },
    { x: defender.x + 20, scale: 0.5, opacity: 1, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightchop',
    { x: defender.x + 20, y: defender.y, z: defender.z - 10, scale: 0.5, opacity: 1, time: 500 },
    { x: defender.x - 15, opacity: 0, time: 800 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'rightchop',
    { x: defender.x + 20, y: defender.y, z: defender.z - 10, scale: 0.5, opacity: 0.3, time: 575 },
    { x: defender.x - 15, opacity: 0, time: 850 },
    'decel',
    'fade',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-15), y: defender.y, z: defender.behind(15), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/** PS `foresight` → `dance` template. */
const foresight: MoveAnim = dance;

/** PS `freezedry` — four icicles + four frosty wisps wash over the defender. */
const freezedry: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 100 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 200 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 600 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 300 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 700 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2.4, opacity: 0.3 },
    {
      x: defender.x + 10,
      y: defender.y + 5,
      z: defender.behind(30),
      scale: 3,
      opacity: 0.6,
      time: 400,
    },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2.4, opacity: 0.3, time: 100 },
    {
      x: defender.x - 10,
      y: defender.y - 5,
      z: defender.behind(30),
      scale: 3,
      opacity: 0.6,
      time: 500,
    },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2.4, opacity: 0.3, time: 200 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 3, opacity: 0.6, time: 600 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2.4, opacity: 0.3, time: 300 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 3, opacity: 0.6, time: 700 },
    'linear',
    'explode',
  );
};

/** PS `furyattack` → `xattack` template. */
const furyattack: MoveAnim = xattack;

/** PS `furycutter` → `slashattack` template. */
const furycutter: MoveAnim = slashattack;

/** PS `furyswipes` — four claw slashes (two left, two right) + an xattack lunge. */
const furyswipes: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'leftslash',
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 1.5, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftslash',
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 1.5, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightslash',
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 700 },
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 1.5, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightslash',
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 1, opacity: 1, time: 700 },
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 1.5, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  xattack(scene, actors);
};

/**
 * PS `gigaimpact` — a dark wash, impact rings, and a hard accelerating tackle. PS
 * drew two attacker-sprite streaks (`showEffect(attacker.sp, …)`); omitted (no
 * sprite-as-effect in our engine) — the attacker's lunge below preserves the read.
 */
const gigaimpact: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.2);
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 4, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted.
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `grassknot` — two energy orbs + two leaves burst up under the defender. */
const grassknot: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: defender.x + 30, y: defender.y - 30, z: defender.z, scale: 0, opacity: 0.6, time: 50 },
    { scale: 3, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x - 30, y: defender.y - 40, z: defender.z, scale: 0, opacity: 0.6, time: 200 },
    { scale: 3, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'leaf1',
    { x: defender.x + 30, y: defender.y - 30, z: defender.z, scale: 0, opacity: 0.6, time: 50 },
    { y: defender.y - 40, scale: 3, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'leaf2',
    { x: defender.x - 30, y: defender.y - 40, z: defender.z, scale: 0, opacity: 0.6, time: 200 },
    { y: defender.y - 50, scale: 3, opacity: 0 },
    'decel',
  );
};

/** PS `grasswhistle` — a green wash + the `sound` ripple. */
const grasswhistle: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#9AB440', 900, 0.3);
  sound(scene, actors);
};

/** PS `grassyterrain` — a green wash + a `dance` shimmy. */
const grassyterrain: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#9AB440', 1000, 0.3);
  dance(scene, actors);
};

/**
 * PS `gravity` — intentionally no animation (PS leaves it empty so it doesn't
 * conflict with the field gravity animation and to suppress the default wisp).
 */
const gravity: MoveAnim = () => {
  // no-op (matches PS)
};

/** PS `growl` → `sound` template. */
const growl: MoveAnim = sound;

/**
 * PS `growth` — the user swells. PS drew two attacker-sprite streaks
 * (`showEffect(attacker.sp, …)`); omitted (no sprite-as-effect) — the scale-up
 * lunge below preserves the read.
 */
const growth: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  // PS drew two attacker-sprite streaks here (showEffect(attacker.sp, …)); omitted.
  attacker.anim({ scale: 1.25, time: 600 }, 'linear');
  attacker.anim({ time: 300 }, 'accel');
};

/** PS `guillotine` → `xattack` template. */
const guillotine: MoveAnim = xattack;

/** PS `gunkshot` — a spray of six poison wisps splatters across the defender. */
const gunkshot: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.6 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 2, opacity: 0.3, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.6, time: 75 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 2, opacity: 0.3, time: 275 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.6, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 2, opacity: 0.3, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.6, time: 225 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 2, opacity: 0.3, time: 425 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.6, time: 300 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 2, opacity: 0.3, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.6, time: 375 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 2, opacity: 0.3, time: 575 },
    'linear',
    'explode',
  );
};

/** PS `gyroball` → `spinattack` template. */
const gyroball: MoveAnim = spinattack;

/** PS `hail` → `dance` template. */
const hail: MoveAnim = dance;

/** PS `hammerarm` — a heavy fist + shadowburst slams down on the defender. */
const hammerarm: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'fist1',
    {
      x: defender.x,
      y: defender.y + 80,
      z: defender.behind(-15),
      scale: 1.2,
      opacity: 0.8,
      time: 400,
    },
    { y: defender.y - 10, z: defender.z, scale: 1.6, opacity: 1, time: 500 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 450 },
    { scale: 2, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 1, time: 500 },
    { x: defender.x + 70, scale: 0.8, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 1, time: 500 },
    { x: defender.x - 70, scale: 0.8, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.behind(20), time: 100 });
  attacker.anim({ time: 600 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ y: defender.y - 30, z: defender.behind(20), yscale: 0.5, time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/**
 * PS `hardpress` — mistballs squeeze in from both sides + mud puffs, the defender
 * is crushed thin. PS passed a 5th `{ filter: 'saturate(0)' }` arg to the mistball
 * showEffects; our showEffect has no filter slot, so the arg is dropped (motion
 * preserved). PS's `attacker.delay`/`defender.delay` are soft no-ops in our Actor.
 */
const hardpress: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'mistball',
    { x: defender.leftof(-60), y: defender.y + 20, z: defender.z, scale: 0.6, time: 0 },
    { y: defender.y, time: 200 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'mistball',
    { x: defender.leftof(-60), y: defender.y, z: defender.z, scale: 0.6, time: 200 },
    { time: 400 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'mistball',
    { x: defender.leftof(60), y: defender.y + 20, z: defender.z, scale: 0.6, time: 200 },
    { y: defender.y, time: 400 },
    'accel',
    'fade',
  );

  const xPos = [1, 0, -1, 0];
  const zPos = [0, 1, 0, -1];
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'mudwisp',
      {
        x: defender.leftof(-60),
        y: defender.y - 15,
        z: defender.z,
        scale: 0.5,
        opacity: 0.6,
        time: 200,
      },
      {
        x: defender.leftof(-60) + 15 * xPos[i],
        z: defender.z + 20 * zPos[i],
        scale: 0.2,
        time: 400,
      },
      'ballistic2Under',
      'fade',
    );
    scene.showEffect(
      'mudwisp',
      {
        x: defender.leftof(60),
        y: defender.y - 15,
        z: defender.z,
        scale: 0.5,
        opacity: 0.6,
        time: 400,
      },
      {
        x: defender.leftof(60) + 15 * xPos[i],
        z: defender.z + 20 * zPos[i],
        scale: 0.2,
        time: 600,
      },
      'ballistic2Under',
      'fade',
    );
  }

  scene.showEffect(
    'mistball',
    { x: defender.leftof(-60), y: defender.y, z: defender.z, scale: 0.6, time: 450 },
    { x: defender.leftof(-30), time: 650 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'mistball',
    { x: defender.leftof(60), y: defender.y, z: defender.z, scale: 0.6, time: 450 },
    { x: defender.leftof(30), time: 650 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'mistball',
    { x: defender.leftof(-30), y: defender.y, z: defender.z, scale: 0.6, time: 650 },
    { time: 850 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'mistball',
    { x: defender.leftof(30), y: defender.y, z: defender.z, scale: 0.6, time: 650 },
    { time: 850 },
    'linear',
    'fade',
  );

  attacker.delay(450);
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 200 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');

  defender.delay(450);
  defender.anim({ xscale: 0.6, yscale: 1.3, time: 200 });
  defender.delay(200);
  defender.anim({ xscale: 1, yscale: 1, time: 150 });
};

/** PS `harden` → `selfstatus` template. */
const harden: MoveAnim = selfstatus;

/** PS `haze` — a dark wash + sixteen black wisps fan out from the user. */
const haze: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  const xf = [1, -1, 1, -1];
  const yf = [1, -1, -1, 1];
  const xf2 = [1, 0, -1, 0];
  const yf2 = [0, 1, 0, -1];

  scene.backgroundEffect('#000000', 1000, 0.3);
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'blackwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 1 },
      {
        x: attacker.x + 120 * xf[i],
        y: attacker.y,
        z: attacker.z + 68 * yf[i],
        scale: 1,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
    scene.showEffect(
      'blackwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 1 },
      {
        x: attacker.x + 113 * xf2[i],
        y: attacker.y,
        z: attacker.z + 97 * yf2[i],
        scale: 1,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
    scene.showEffect(
      'blackwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 1 },
      {
        x: attacker.x + 120 * xf[i],
        y: attacker.y,
        z: attacker.z + 68 * yf[i],
        scale: 1,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
    scene.showEffect(
      'blackwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 1 },
      {
        x: attacker.x + 113 * xf2[i],
        y: attacker.y,
        z: attacker.z + 97 * yf2[i],
        scale: 1,
        opacity: 0,
        time: 800,
      },
      'decel',
      'fade',
    );
  }
};

/** PS `headbutt` → `contactattack` template. */
const headbutt: MoveAnim = contactattack;

/** PS `healbell` → `selfstatus` template. */
const healbell: MoveAnim = selfstatus;

/** PS `healblock` — wisps sweep back and forth across the defender. */
const healblock: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 400 },
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 400 },
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 600 },
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 600 },
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 800 },
    'linear',
    'fade',
  );
};

/** PS `healpulse` — a soft mistball pulse from user to target + wisps fanning out. */
const healpulse: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  const xf = [1, -1, 1, -1];
  const yf = [1, -1, -1, 1];
  const xf2 = [1, 0, -1, 0];
  const yf2 = [0, 1, 0, -1];

  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.4, time: 0 },
    { scale: 8, opacity: 0.1, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 300 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 500 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 800 },
    'linear',
  );
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 1 },
      {
        x: attacker.x + 240 * xf[i],
        y: attacker.y,
        z: attacker.z + 137 * yf[i],
        scale: 0.3,
        opacity: 0.5,
        time: 800,
      },
      'accel',
    );
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 1 },
      {
        x: attacker.x + 339 * xf2[i],
        y: attacker.y,
        z: attacker.z + 194 * yf2[i],
        scale: 0.3,
        opacity: 0.5,
        time: 800,
      },
      'accel',
    );
  }
};

/**
 * PS `healingwish` — a wisp rises off the user (the wish departs); the descending
 * residual is an end-of-turn anim. We port the main `anim`; the `residualAnim` is
 * not part of our single-MoveAnim surface, so it is dropped (noted) — the rising
 * wish reads the move on its own.
 */
const healingwish: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 1 },
    { x: attacker.x, y: attacker.y + 130, z: attacker.z, opacity: 0 },
    'accel',
  );
};

/** PS `heartstamp` — a heart bursts over the defender + a contact lunge. */
const heartstamp: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'heart',
    {
      x: defender.leftof(-20),
      y: defender.y + 15,
      z: defender.z,
      scale: 1,
      opacity: 0.5,
      time: 450,
    },
    { scale: 4, opacity: 0, time: 700 },
    'linear',
  );
  contactattack(scene, actors);
};

/**
 * PS `heatwave` — fireballs sweep the field while the defender rocks. PS iterated
 * over all `defenders` (multi-target); our 1v1 actors tuple is `[attacker, defender]`,
 * so we apply the per-defender rock + the single fireball sweep to the lone defender.
 */
const heatwave: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  defender.delay(125);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 150 }, 'swing');

  scene.backgroundEffect('#CC3300', 900, 0.1);
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2.5, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 150 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2.5, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 300 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2.5, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `heavyslam` — a heavy ballistic flop with a shadowburst that squashes flat. */
const heavyslam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 450 },
    { scale: 2, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 1, time: 500 },
    { x: defender.x + 70, scale: 0.8, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 1, time: 500 },
    { x: defender.x - 70, scale: 0.8, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.behind(20), time: 100 });
  attacker.anim({ time: 600 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ y: defender.y - 30, z: defender.behind(20), yscale: 0.5, time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `hex` — a dark wash, three poison wisps, and three blue flames rising. */
const hex: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.3);
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 40, y: defender.y, z: defender.z, scale: 0, opacity: 1 },
    { scale: 3, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 40, y: defender.y - 20, z: defender.z, scale: 0, opacity: 1, time: 150 },
    { scale: 3, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 10, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 300 },
    { scale: 3, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'bluefireball',
    { x: defender.x + 40, y: defender.y + 30, z: defender.z, scale: 0.8, opacity: 0.5, time: 0 },
    { y: defender.y + 60, opacity: 0, time: 400 },
    'accel',
  );
  scene.showEffect(
    'bluefireball',
    { x: defender.x - 40, y: defender.y + 30, z: defender.z, scale: 0.8, opacity: 0.5, time: 200 },
    { y: defender.y + 60, opacity: 0, time: 600 },
    'accel',
  );
  scene.showEffect(
    'bluefireball',
    { x: defender.x, y: defender.y + 40, z: defender.z, scale: 0.8, opacity: 0.5, time: 400 },
    { y: defender.y + 60, opacity: 0, time: 800 },
    'accel',
  );
};

/** PS `hiddenpower` — eight electro orbs fan out from the user in all directions. */
const hiddenpower: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  const xf = [1, -1, 1, -1];
  const yf = [1, -1, -1, 1];
  const xf2 = [1, 0, -1, 0];
  const yf2 = [0, 1, 0, -1];

  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'electroball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 1 },
      {
        x: attacker.x + 240 * xf[i],
        y: attacker.y,
        z: attacker.z + 137 * yf[i],
        scale: 1,
        opacity: 0.5,
        time: 800,
      },
      'accel',
      'fade',
    );
    scene.showEffect(
      'electroball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 1 },
      {
        x: attacker.x + 339 * xf2[i],
        y: attacker.y,
        z: attacker.z + 194 * yf2[i],
        scale: 1,
        opacity: 0.5,
        time: 800,
      },
      'accel',
      'fade',
    );
  }
};

/**
 * PS `highjumpkick` — a leaping kick. PS drew two attacker-sprite arcs
 * (`showEffect(attacker.sp, …)`); omitted (no sprite-as-effect) — the attacker's
 * ballistic leap below preserves the read.
 */
const highjumpkick: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS drew two attacker-sprite arcs here (showEffect(attacker.sp, …)); omitted.
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { x: defender.x, y: defender.y + 10, z: defender.behind(15), scale: 2, opacity: 0, time: 900 },
    'linear',
  );
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.7, time: 500 },
    { scale: 3, opacity: 0, time: 750 },
    'linear',
    'fade',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 170, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 200 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(500);
  defender.anim({ y: defender.y - 5, z: defender.behind(40), yscale: 0.9, time: 300 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/* ──────────────────────────────────────────────────────────────────────────
 * The batch table, keyed by Showdown move id (toID form).
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_BATCH_3: Record<string, MoveAnim> = {
  flipturn,
  fly,
  focusblast,
  focusenergy,
  focuspunch,
  followme,
  forcepalm,
  foresight,
  freezedry,
  furyattack,
  furycutter,
  furyswipes,
  gigaimpact,
  grassknot,
  grasswhistle,
  grassyterrain,
  gravity,
  growl,
  growth,
  guillotine,
  gunkshot,
  gyroball,
  hail,
  hammerarm,
  hardpress,
  harden,
  haze,
  headbutt,
  healbell,
  healblock,
  healpulse,
  healingwish,
  heartstamp,
  heatwave,
  heavyslam,
  hex,
  hiddenpower,
  highjumpkick,
};
